import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../../../config/api';
import { resizeImage, convertURLToBase64, getBase64Size, isValidImageURL } from './playerManagementUtils';
import SportsManagerModal from './SportsManagerModal';

const PlayerManagement = React.forwardRef((props, ref) => {
  const [players, setPlayers] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingPlayer, setEditingPlayer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState('');
  
  // SIMPLIFIED: Sports management state - only sport name needed
  const [sports, setSports] = useState([]);
  const [showSportsManager, setShowSportsManager] = useState(false);
  const [newSport, setNewSport] = useState('');
  const [editingSport, setEditingSport] = useState(null);
  const [editSportValue, setEditSportValue] = useState('');
  const [sportsLoading, setSportsLoading] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    sport: '',
    team: '',
    position: '',
    image: '',
    imageUrl: '',
    difficulty: 'medium',
    isActive: true
  });

  // Expose methods to parent component
  React.useImperativeHandle(ref, () => ({
    openAddForm: () => setShowAddForm(true)
  }));

  const difficulties = [
    { value: 'easy', label: 'Easy', color: 'bg-green-100 text-green-800' },
    { value: 'medium', label: 'Medium', color: 'bg-yellow-100 text-yellow-800' },
    { value: 'hard', label: 'Hard', color: 'bg-red-100 text-red-800' }
  ];

  // Load sports from database
  useEffect(() => {
    fetchPlayers();
    fetchSports();
  }, []);

  const fetchPlayers = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/api/admin/players`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setPlayers(data);
      }
    } catch (error) {
      console.error('Error fetching players:', error);
    }
    setLoading(false);
  };

  // Fetch sports from database
  const fetchSports = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/api/admin/sports`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setSports(data);
      } else {
        console.error('Failed to fetch sports:', response.status, response.statusText);
        setMessage('Error loading sports from server');
      }
    } catch (error) {
      console.error('Error fetching sports:', error);
      setMessage('Network error loading sports');
    }
  };

  // ENHANCED: Add sport with better error handling and debugging
  const handleAddSport = async () => {
    if (!newSport.trim()) {
      setMessage('Please enter a sport name');
      return;
    }

    setSportsLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      
      if (!token) {
        setMessage('Authentication token missing. Please login again.');
        setSportsLoading(false);
        return;
      }

      const sportName = newSport.trim().toLowerCase();
      const displayName = newSport.trim().charAt(0).toUpperCase() + newSport.trim().slice(1).toLowerCase();

      console.log('Adding sport:', { name: sportName, displayName: displayName });

      const response = await fetch(`${API_BASE_URL}/api/admin/sports`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: sportName,
          displayName: displayName
        }),
      });

      console.log('Response status:', response.status);
      console.log('Response headers:', response.headers);

      const data = await response.json();
      console.log('Response data:', data);

      if (response.ok) {
        await fetchSports(); // Refresh sports list
        setNewSport('');
        setMessage('Sport added successfully!');
        setTimeout(() => setMessage(''), 3000);
      } else {
        // Enhanced error handling
        const errorMessage = data.message || `Server error: ${response.status}`;
        console.error('Server error response:', data);
        setMessage(errorMessage);
      }
    } catch (error) {
      console.error('Network error adding sport:', error);
      setMessage(`Network error: ${error.message}. Please check your connection and try again.`);
    }
    setSportsLoading(false);
  };

  const handleEditSport = (sport) => {
    setEditingSport(sport._id);
    setEditSportValue(sport.displayName);
  };

  const handleSaveEditSport = async () => {
    if (!editSportValue.trim()) {
      setMessage('Please enter a sport name');
      return;
    }

    setSportsLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const sportName = editSportValue.trim().toLowerCase();
      const displayName = editSportValue.trim().charAt(0).toUpperCase() + editSportValue.trim().slice(1).toLowerCase();

      const response = await fetch(`${API_BASE_URL}/api/admin/sports/${editingSport}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: sportName,
          displayName: displayName
        }),
      });

      const data = await response.json();
      if (response.ok) {
        await fetchSports(); // Refresh sports list
        setEditingSport(null);
        setEditSportValue('');
        setMessage('Sport updated successfully!');
        setTimeout(() => setMessage(''), 3000);
      } else {
        setMessage(data.message || `Error updating sport: ${response.status}`);
      }
    } catch (error) {
      console.error('Error updating sport:', error);
      setMessage(`Network error: ${error.message}`);
    }
    setSportsLoading(false);
  };

  const handleRemoveSport = async (sport) => {
    // Check sport usage first
    try {
      const token = localStorage.getItem('adminToken');
      const usageResponse = await fetch(`${API_BASE_URL}/api/admin/sports/${sport._id}/usage`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (usageResponse.ok) {
        const usageData = await usageResponse.json();
        
        if (usageData.playersCount > 0) {
          const confirmDelete = window.confirm(
            `${usageData.playersCount} player(s) are using this sport. Removing this sport will not delete the players, but they will need to be updated with a different sport. Do you want to continue?`
          );
          if (!confirmDelete) return;
        } else {
          if (!window.confirm(`Are you sure you want to remove "${sport.displayName}" from the sports list?`)) {
            return;
          }
        }

        // Proceed with deletion
        setSportsLoading(true);
        const deleteResponse = await fetch(`${API_BASE_URL}/api/admin/sports/${sport._id}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        const deleteData = await deleteResponse.json();
        if (deleteResponse.ok) {
          await fetchSports(); // Refresh sports list
          setMessage(`Sport "${sport.displayName}" removed successfully!`);
          setTimeout(() => setMessage(''), 3000);
        } else {
          setMessage(deleteData.message || `Error removing sport: ${deleteResponse.status}`);
        }
      }
    } catch (error) {
      console.error('Error removing sport:', error);
      setMessage(`Network error: ${error.message}`);
    }
    setSportsLoading(false);
  };

  // Keep all other existing functions unchanged...
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.sport || (!formData.image && !formData.imageUrl)) {
      setMessage('Please fill in all required fields');
      return;
    }

    setUploading(true);
    setMessage('');

    try {
      const token = localStorage.getItem('adminToken');
      let imageData = formData.image;
      let imageUrl = formData.imageUrl;
      let size = 0;

      if (formData.imageUrl && !formData.image) {
        if (!isValidImageURL(formData.imageUrl)) {
          setMessage('Please provide a valid image URL');
          setUploading(false);
          return;
        }
        imageData = await convertURLToBase64(formData.imageUrl);
        size = getBase64Size(imageData);
      } else if (formData.image) {
        size = getBase64Size(formData.image);
      }

      if (size < 5000) {
        setMessage('Warning: Image might be too small or unclear. Please upload a clear, high-quality image.');
      }

      const url = editingPlayer 
        ? `${API_BASE_URL}/api/admin/players/${editingPlayer._id}`
        : `${API_BASE_URL}/api/admin/players`;
      const method = editingPlayer ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          ...formData,
          image: imageData,
          imageUrl: imageUrl,
          size: size
        }),
      });

      const data = await response.json();
      if (response.ok) {
        fetchPlayers();
        resetForm();
        setMessage(`Player ${editingPlayer ? 'updated' : 'created'} successfully!`);
        setTimeout(() => setMessage(''), 3000);
      } else {
        setMessage(data.message || 'Error saving player');
      }
    } catch (error) {
      console.error('Error saving player:', error);
      setMessage('Error saving player. Please try again.');
    }
    setUploading(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this player? This will also remove all related drawing results.')) return;

    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/api/admin/players/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        fetchPlayers();
        setMessage('Player deleted successfully!');
        setTimeout(() => setMessage(''), 3000);
      }
    } catch (error) {
      console.error('Error deleting player:', error);
    }
  };

  const handleToggleStatus = async (id) => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/api/admin/players/${id}/toggle`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        fetchPlayers();
        setMessage('Player status updated successfully!');
        setTimeout(() => setMessage(''), 3000);
      }
    } catch (error) {
      console.error('Error toggling player status:', error);
    }
  };

  const handleEdit = (player) => {
    setFormData({
      name: player.name,
      sport: player.sport,
      team: player.team || '',
      position: player.position || '',
      image: player.image,
      imageUrl: player.imageUrl || '',
      difficulty: player.difficulty,
      isActive: player.isActive
    });
    setEditingPlayer(player);
    setShowAddForm(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      sport: '',
      team: '',
      position: '',
      image: '',
      imageUrl: '',
      difficulty: 'medium',
      isActive: true
    });
    setShowAddForm(false);
    setEditingPlayer(null);
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setMessage('Please select a valid image file');
      return;
    }

    setUploading(true);
    setMessage('Processing image...');

    try {
      const resizedImage = await resizeImage(file, 100);
      setFormData(prev => ({
        ...prev,
        image: resizedImage,
        imageUrl: ''
      }));
      setMessage('Image processed successfully!');
      setTimeout(() => setMessage(''), 2000);
    } catch (error) {
      console.error('Error processing image:', error);
      setMessage('Error processing image. Please try again.');
    }
    setUploading(false);
  };

  const handleUrlChange = (e) => {
    setFormData(prev => ({
      ...prev,
      imageUrl: e.target.value,
      image: ''
    }));
  };

  const handleCloseSportsManager = () => {
    setShowSportsManager(false);
    setEditingSport(null);
    setEditSportValue('');
    setNewSport('');
  };

  const handleCancelEditSport = () => {
    setEditingSport(null);
    setEditSportValue('');
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getDifficultyBadge = (difficulty) => {
    const diff = difficulties.find(d => d.value === difficulty);
    return diff ? `${diff.color} px-2 py-1 text-xs rounded-full font-medium` : 'bg-gray-100 text-gray-800 px-2 py-1 text-xs rounded-full font-medium';
  };

  return (
    <div className="p-3 sm:p-4 lg:p-6">
      {/* **RESPONSIVE: Header** */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 sm:mb-6 space-y-3 sm:space-y-0">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Player Management</h2>
          <p className="text-sm sm:text-base text-gray-600 mt-1">Manage players for the drawing game</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 w-full sm:w-auto">
          <button
            onClick={() => setShowSportsManager(true)}
            className="bg-green-600 hover:bg-green-700 text-white px-3 sm:px-4 py-2 rounded-lg flex items-center justify-center gap-2 text-sm sm:text-base"
          >
            <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <span className="hidden sm:inline">Manage Sports</span>
            <span className="sm:hidden">Sports</span>
          </button>
          <button
            onClick={() => setShowAddForm(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white px-3 sm:px-4 py-2 rounded-lg flex items-center justify-center gap-2 text-sm sm:text-base"
          >
            <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            <span className="hidden sm:inline">Add Player</span>
            <span className="sm:hidden">Add</span>
          </button>
        </div>
      </div>

      {/* **RESPONSIVE: Enhanced Message Display** */}
      {message && (
        <div className={`mb-4 sm:mb-6 p-3 sm:p-4 rounded-lg text-sm sm:text-base ${message.includes('successfully') || message.includes('processed') 
          ? 'bg-green-100 border border-green-200 text-green-700' 
          : message.includes('Warning') 
            ? 'bg-yellow-100 border border-yellow-200 text-yellow-700'
            : 'bg-red-100 border border-red-200 text-red-700'
        }`}>
          {message}
        </div>
      )}

      <SportsManagerModal
        show={showSportsManager}
        onClose={handleCloseSportsManager}
        newSport={newSport}
        setNewSport={setNewSport}
        handleAddSport={handleAddSport}
        sportsLoading={sportsLoading}
        sports={sports}
        editingSport={editingSport}
        editSportValue={editSportValue}
        setEditSportValue={setEditSportValue}
        handleSaveEditSport={handleSaveEditSport}
        onCancelEdit={handleCancelEditSport}
        handleEditSport={handleEditSport}
        handleRemoveSport={handleRemoveSport}
      />

      {/* **RESPONSIVE: Add/Edit Form** */}
      {showAddForm && (
        <div className="bg-white rounded-lg shadow p-3 sm:p-4 lg:p-6 mb-4 sm:mb-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-3 sm:mb-4">
            <h3 className="text-base sm:text-lg font-semibold mb-2 sm:mb-0">
              {editingPlayer ? 'Edit Player' : 'Add New Player'}
            </h3>
            <button
              onClick={resetForm}
              className="text-gray-500 hover:text-gray-700 self-end sm:self-auto"
            >
              <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                  Player Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>

              {/* **RESPONSIVE: Sport dropdown uses database sports** */}
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                  Sport *
                </label>
                <select
                  value={formData.sport}
                  onChange={(e) => setFormData(prev => ({ ...prev, sport: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                >
                  <option value="">Select Sport</option>
                  {sports.map(sport => (
                    <option key={sport._id} value={sport.name}>
                      {sport.displayName}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                  Team
                </label>
                <input
                  type="text"
                  value={formData.team}
                  onChange={(e) => setFormData(prev => ({ ...prev, team: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                  Position
                </label>
                <input
                  type="text"
                  value={formData.position}
                  onChange={(e) => setFormData(prev => ({ ...prev, position: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                  Difficulty
                </label>
                <select
                  value={formData.difficulty}
                  onChange={(e) => setFormData(prev => ({ ...prev, difficulty: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {difficulties.map(diff => (
                    <option key={diff.value} value={diff.value}>
                      {diff.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="isActive"
                  checked={formData.isActive}
                  onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                  className="w-4 h-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="isActive" className="ml-2 text-xs sm:text-sm text-gray-700">
                  Active (visible in game)
                </label>
              </div>
            </div>

            {/* **RESPONSIVE: Image Upload** */}
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                Player Image * (Max 100KB, clear image recommended)
              </label>
              
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-3 sm:p-4">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4">
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-600 mb-1 sm:mb-2">
                      Upload File
                    </label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleFileChange}
                      className="w-full text-xs sm:text-sm text-gray-500 file:mr-2 sm:file:mr-4 file:py-1 sm:file:py-2 file:px-2 sm:file:px-4 file:rounded-full file:border-0 file:text-xs sm:file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-600 mb-1 sm:mb-2">
                      Or Image URL
                    </label>
                    <input
                      type="url"
                      value={formData.imageUrl}
                      onChange={handleUrlChange}
                      placeholder="https://example.com/image.jpg"
                      className="w-full border border-gray-300 rounded px-3 py-2 text-xs sm:text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>

                {(formData.image || formData.imageUrl) && (
                  <div className="mt-3 sm:mt-4 text-center">
                    <img
                      src={formData.image || formData.imageUrl}
                      alt="Preview"
                      className="max-w-24 max-h-24 sm:max-w-32 sm:max-h-32 mx-auto rounded border"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'block';
                      }}
                    />
                    <div className="text-red-500 text-xs sm:text-sm hidden">
                      Invalid image or URL
                    </div>
                    {formData.image && (
                      <p className="text-xs text-gray-500 mt-1">
                        Size: {formatFileSize(getBase64Size(formData.image))}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <button
                type="submit"
                disabled={uploading}
                className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white px-4 py-2 rounded-lg font-medium text-sm sm:text-base"
              >
                {uploading ? 'Processing...' : editingPlayer ? 'Update Player' : 'Add Player'}
              </button>
              <button
                type="button"
                onClick={resetForm}
                className="px-4 sm:px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm sm:text-base"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* **RESPONSIVE: Players List** */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-3 sm:px-4 lg:px-6 py-3 sm:py-4 border-b border-gray-200">
          <h3 className="text-base sm:text-lg font-medium text-gray-900">
            Players ({players.length})
          </h3>
        </div>

        {loading ? (
          <div className="text-center py-8 sm:py-12">
            <div className="inline-block animate-spin rounded-full h-6 w-6 sm:h-8 sm:w-8 border-b-2 border-blue-600"></div>
            <p className="mt-3 sm:mt-4 text-sm sm:text-base text-gray-600">Loading players...</p>
          </div>
        ) : players.length === 0 ? (
          <div className="text-center py-8 sm:py-12 px-4">
            <svg className="w-10 h-10 sm:w-12 sm:h-12 mx-auto text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
            <p className="mt-3 sm:mt-4 text-sm sm:text-base text-gray-600">No players found. Add your first player!</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            {/* **RESPONSIVE: Desktop Table View (hidden on mobile)** */}
            <div className="hidden lg:block">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Player
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Sport
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Team/Position
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Difficulty
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {players.map(player => (
                    <tr key={player._id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="w-12 h-12 bg-gray-200 rounded-lg overflow-hidden mr-4">
                            <img
                              src={player.image}
                              alt={player.name}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                              }}
                            />
                            <div className="w-full h-full flex items-center justify-center text-gray-400 text-xs" style={{display: 'none'}}>
                              No Image
                            </div>
                          </div>
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {player.name}
                            </div>
                            <div className="text-sm text-gray-500">
                              {formatFileSize(player.size || 0)}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <span className="capitalize">
                          {sports.find(s => s.name === player.sport)?.displayName || player.sport}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {player.team && (
                          <div>{player.team}</div>
                        )}
                        {player.position && (
                          <div className="text-gray-500">{player.position}</div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={getDifficultyBadge(player.difficulty)}>
                          {player.difficulty.toUpperCase()}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs rounded-full font-medium ${
                          player.isActive 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {player.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(player.createdAt).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleEdit(player)}
                            className="text-blue-600 hover:text-blue-900"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleToggleStatus(player._id)}
                            className={`${
                              player.isActive ? 'text-yellow-600 hover:text-yellow-900' : 'text-green-600 hover:text-green-900'
                            }`}
                          >
                            {player.isActive ? 'Deactivate' : 'Activate'}
                          </button>
                          <button
                            onClick={() => handleDelete(player._id)}
                            className="text-red-600 hover:text-red-900"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* **RESPONSIVE: Mobile Card View (visible on mobile and tablets)** */}
            <div className="lg:hidden">
              <div className="space-y-3 sm:space-y-4 p-3 sm:p-4">
                {players.map(player => (
                  <div key={player._id} className="bg-gray-50 rounded-lg p-3 sm:p-4">
                    <div className="flex items-start space-x-3 sm:space-x-4 mb-3">
                      <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gray-200 rounded-lg overflow-hidden flex-shrink-0">
                        <img
                          src={player.image}
                          alt={player.name}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                        <div className="w-full h-full flex items-center justify-center text-gray-400 text-xs" style={{display: 'none'}}>
                          No Image
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="text-sm sm:text-base font-medium text-gray-900 truncate">
                          {player.name}
                        </h4>
                        <div className="text-xs sm:text-sm text-gray-500 space-y-1">
                          <div>
                            Sport: <span className="capitalize font-medium">
                              {sports.find(s => s.name === player.sport)?.displayName || player.sport}
                            </span>
                          </div>
                          {player.team && (
                            <div>Team: <span className="font-medium">{player.team}</span></div>
                          )}
                          {player.position && (
                            <div>Position: <span className="font-medium">{player.position}</span></div>
                          )}
                          <div>Size: {formatFileSize(player.size || 0)}</div>
                          <div>Created: {new Date(player.createdAt).toLocaleDateString()}</div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-2 sm:space-y-0">
                      <div className="flex space-x-2">
                        <span className={getDifficultyBadge(player.difficulty)}>
                          {player.difficulty.toUpperCase()}
                        </span>
                        <span className={`px-2 py-1 text-xs rounded-full font-medium ${
                          player.isActive 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {player.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                      
                      <div className="flex flex-wrap gap-2 text-xs sm:text-sm font-medium">
                        <button
                          onClick={() => handleEdit(player)}
                          className="text-blue-600 hover:text-blue-900 px-2 py-1 bg-blue-50 rounded"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleToggleStatus(player._id)}
                          className={`px-2 py-1 rounded ${
                            player.isActive 
                              ? 'text-yellow-600 hover:text-yellow-900 bg-yellow-50' 
                              : 'text-green-600 hover:text-green-900 bg-green-50'
                          }`}
                        >
                          {player.isActive ? 'Deactivate' : 'Activate'}
                        </button>
                        <button
                          onClick={() => handleDelete(player._id)}
                          className="text-red-600 hover:text-red-900 px-2 py-1 bg-red-50 rounded"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
});

export default PlayerManagement;
